import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Base_URL2 } from '../url';
import '../Styling/AzureInvoice_taxReport.css';

// ════════════════════════════════════════════
// API LAYER
// ════════════════════════════════════════════
const AIT_BASE = `${Base_URL2}/vim-invoiceUpload`;

const aitUploadInvoice  = (file) => {
    const form = new FormData();
    form.append('file', file);
    return axios.post(`${AIT_BASE}/upload`, form);
};

const aitGetInvoices    = () => axios.get(AIT_BASE);
const aitAnalyzeInvoice = (id) => axios.post(`${AIT_BASE}/${id}/analyze`);
const aitGetFileUrl     = (id) => `${AIT_BASE}/${id}/file`;


// ════════════════════════════════════════════
// HELPERS
// ════════════════════════════════════════════

// Currency symbol map — fallback to ₹ if null
const getCurrencySymbol = (code) => {
    const map = { INR: '₹', USD: '$', EUR: '€', GBP: '£' };
    return map[code] || '₹';
};

const fmtCurrency = (val, code = null) => {
    if (val == null) return '—';
    const symbol = getCurrencySymbol(code);
    return `${symbol}${Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
};

const fmtDate = (val) => {
    if (!val) return '—';
    return new Date(val).toLocaleDateString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric'
    });
};

// Renders address with \n as line breaks
const AddressBlock = ({ value }) => {
    if (!value) return null;
    return (
        <div className="ait-rs-address">
            {value.split('\n').map((line, i) => (
                <span key={i}>{line}</span>
            ))}
        </div>
    );
};


// ════════════════════════════════════════════
// COMPONENT 1: RESULT PANEL
// ════════════════════════════════════════════
const AzureInvoice_Report_Result = ({ data }) => {

    if (!data) return (
        <div className="ait-panel-right">
            <div className="ait-rs-header">🔍 Extracted Fields</div>
            <div className="ait-rs-empty">
                <span className="ait-rs-empty-icon">📊</span>
                <span>Run analysis to see<br />extracted fields</span>
            </div>
        </div>
    );

    const cc = data.currencyCode;

    return (
        <div className="ait-panel-right">
            <div className="ait-rs-header">🔍 Extracted Fields</div>
            <div className="ait-rs-content">

                {/* ── Invoice Meta ── */}
                <div className="ait-rs-section-title">Invoice Info</div>

                {data.InvoiceId && (
                    <div className="ait-rs-field">
                        <div className="ait-rs-field-label">Invoice #</div>
                        <div className="ait-rs-field-value">{data.InvoiceId}</div>
                    </div>
                )}
                {data.InvoiceDate && (
                    <div className="ait-rs-field">
                        <div className="ait-rs-field-label">Invoice Date</div>
                        <div className="ait-rs-field-value">{fmtDate(data.InvoiceDate)}</div>
                    </div>
                )}
                {data.DueDate && (
                    <div className="ait-rs-field">
                        <div className="ait-rs-field-label">Due Date</div>
                        <div className="ait-rs-field-value">{fmtDate(data.DueDate)}</div>
                    </div>
                )}
                {data.PaymentTerm && (
                    <div className="ait-rs-field">
                        <div className="ait-rs-field-label">Terms</div>
                        <div className="ait-rs-field-value">{data.PaymentTerm}</div>
                    </div>
                )}
                {data.PurchaseOrder && (
                    <div className="ait-rs-field">
                        <div className="ait-rs-field-label">P.O. #</div>
                        <div className="ait-rs-field-value">{data.PurchaseOrder}</div>
                    </div>
                )}
                {data.CurrencyCode && (
                    <div className="ait-rs-field">
                        <div className="ait-rs-field-label">Currency</div>
                        <div className="ait-rs-field-value">{data.CurrencyCode}</div>
                    </div>
                )}

                <hr className="ait-rs-divider" />

                {/* ── Vendor ── */}
                <div className="ait-rs-section-title">Vendor</div>

                {data.VendorName && (
                    <div className="ait-rs-field">
                        <div className="ait-rs-field-label">Name</div>
                        <div className="ait-rs-field-value">{data.VendorName}</div>
                    </div>
                )}
                {data.VendorAddress && (
                    <div className="ait-rs-field">
                        <div className="ait-rs-field-label">Address</div>   
                        <AddressBlock value={data.VendorAddress} />
                    </div>
                )}
                {data.VendorTaxId && (
                    <div className="ait-rs-field">
                        <div className="ait-rs-field-label">GSTIN</div>
                        <div className="ait-rs-field-value ait-rs-mono">{data.VendorTaxId}</div>
                    </div>
                )}

                <hr className="ait-rs-divider" />

                {/* ── Customer ── */}
                <div className="ait-rs-section-title">Bill To</div>

                {data.CustomerName && (
                    <div className="ait-rs-field">
                        <div className="ait-rs-field-label">Name</div>
                        <div className="ait-rs-field-value">{data.CustomerName}</div>
                    </div>
                )}

                {/* customerAddress — renders once backend fix is live */}
                {data.CustomerAddress && (
                    <div className="ait-rs-field">
                        <div className="ait-rs-field-label">Address</div>
                        <AddressBlock value={data.CustomerAddress} />
                    </div>
                )}

                {data.CustomerTaxId && (
                    <div className="ait-rs-field">
                        <div className="ait-rs-field-label">GSTIN</div>
                        <div className="ait-rs-field-value ait-rs-mono">{data.CustomerTaxId}</div>
                    </div>
                )}

                <hr className="ait-rs-divider" />

                {/* ── GST Details ── */}
                {data.GstDetails && (
                    <>
                        <div className="ait-rs-section-title">GST Details</div>

                        {data.GstDetails.PlaceOfSupply && (
                            <div className="ait-rs-field">
                                <div className="ait-rs-field-label">Place of Supply</div>
                                <div className="ait-rs-field-value">{data.GstDetails.PlaceOfSupply}</div>
                            </div>
                        )}

                        {data.GstDetails.IgstRate != null && (
                            <div className="ait-rs-field">
                                <div className="ait-rs-field-label">IGST Rate</div>
                                <div className="ait-rs-field-value ait-rs-gst-rate">{data.GstDetails.IgstRate}%</div>
                            </div>
                        )}
                        {data.GstDetails.IgstAmount != null && (
                            <div className="ait-rs-field">
                                <div className="ait-rs-field-label">IGST Amount</div>
                                <div className="ait-rs-field-value">{fmtCurrency(data.GstDetails.IgstAmount, cc)}</div>
                            </div>
                        )}

                        {data.GstDetails.CgstRate != null && (
                            <div className="ait-rs-field">
                                <div className="ait-rs-field-label">CGST Rate</div>
                                <div className="ait-rs-field-value ait-rs-gst-rate">{data.GstDetails.CgstRate}%</div>
                            </div>
                        )}
                        {data.GstDetails.CgstAmount != null && (
                            <div className="ait-rs-field">
                                <div className="ait-rs-field-label">CGST Amount</div>
                                <div className="ait-rs-field-value">{fmtCurrency(data.GstDetails.CgstAmount, cc)}</div>
                            </div>
                        )}

                        {data.GstDetails.SgstRate != null && (
                            <div className="ait-rs-field">
                                <div className="ait-rs-field-label">SGST Rate</div>
                                <div className="ait-rs-field-value ait-rs-gst-rate">{data.GstDetails.SgstRate}%</div>
                            </div>
                        )}
                        {data.GstDetails.SgstAmount != null && (
                            <div className="ait-rs-field">
                                <div className="ait-rs-field-label">SGST Amount</div>
                                <div className="ait-rs-field-value">{fmtCurrency(data.GstDetails.SgstAmount, cc)}</div>
                            </div>
                        )}

                        <hr className="ait-rs-divider" />
                    </>
                )}

                {/* ── Bank Details ── */}
{data.BankDetails && (
    <>
        <div className="ait-rs-section-title">Bank Details</div>

        {data.BankDetails.BankName && (
            <div className="ait-rs-field">
                <div className="ait-rs-field-label">Bank</div>
                <div className="ait-rs-field-value">{data.BankDetails.BankName}</div>
            </div>
        )}
        {data.BankDetails.AccountNumber && (
            <div className="ait-rs-field">
                <div className="ait-rs-field-label">Account No.</div>
                <div className="ait-rs-field-value ait-rs-mono">
                    {data.BankDetails.AccountNumber}
                </div>
            </div>
        )}
        {data.BankDetails.IfscCode && (
            <div className="ait-rs-field">
                <div className="ait-rs-field-label">IFSC</div>
                <div className="ait-rs-field-value ait-rs-mono">
                    {data.BankDetails.IfscCode}
                </div>
            </div>
        )}

        <hr className="ait-rs-divider" />
    </>
)}
{/* ── Digital Signature ── */}
{data.DigitalSignature && (
    <>
        <div className="ait-rs-section-title">Digital Signature</div>

        {data.DigitalSignature.SignedBy && (
            <div className="ait-rs-field">
                <div className="ait-rs-field-label">Signed By</div>
                <div className="ait-rs-field-value">{data.DigitalSignature.SignedBy}</div>
            </div>
        )}
        {data.DigitalSignature.SignedAt && (
            <div className="ait-rs-field">
                <div className="ait-rs-field-label">Signed At</div>
                <div className="ait-rs-field-value">
                    {new Date(data.DigitalSignature.SignedAt).toLocaleString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                    })}
                </div>
            </div>
        )}

        <hr className="ait-rs-divider" />
    </>
)}

                {/* ── Line Items ── */}
              {data.Items?.length > 0 && (
    <>
        <div className="ait-rs-section-title">Line Items</div>

        {data.Items.map((item, i) => (
            <div className="ait-rs-lineitem-card" key={i}>

                <div
                    className="ait-rs-lineitem-desc"
                    title={item.Description}
                >
                    {item.Description || '—'}
                </div>

                <div className="ait-rs-lineitem-meta">

                    {item.ProductCode && (
                        <span className="ait-rs-lineitem-hsn">
                            HSN: {item.ProductCode}
                        </span>
                    )}

                    {item.Quantity != null && (
                        <span>Qty: {item.Quantity}</span>
                    )}

                    {item.UnitPrice != null && (
                        <span>
                            Rate: {fmtCurrency(item.UnitPrice, cc)}
                        </span>
                    )}

                    {item.Tax != null && (
                        <span className="ait-rs-tax-pill">
                            Tax: {fmtCurrency(item.Tax, cc)}
                        </span>
                    )}
                </div>

                <div className="ait-rs-lineitem-amount">
                    {fmtCurrency(item.Amount, cc)}
                </div>
            </div>
        ))}
    </>
)}

                {/* ── Totals ── */}
                <div className="ait-rs-totals">
                    {data.SubTotal != null && (
                        <div className="ait-rs-total-row">
                            <span>Subtotal</span>
                            <span>{fmtCurrency(data.SubTotal, cc)}</span>
                        </div>
                    )}
                    {data.TotalTax != null && (
                        <div className="ait-rs-total-row">
                            <span>
                                {data.GstDetails?.igstAmount != null && data.GstDetails?.cgstAmount == null
                                    ? `IGST (${data.GstDetails.igstRate}%)`
                                    : data.GstDetails?.cgstAmount != null
                                        ? 'CGST + SGST'
                                        : 'Tax'}
                            </span>
                            <span>{fmtCurrency(data.TotalTax, cc)}</span>
                        </div>
                    )}
                    {data.InvoiceTotal != null && (
                        <div className="ait-rs-total-row ait-rs-total-row--grand">
                            <span>Invoice Total</span>
                            <span>{fmtCurrency(data.InvoiceTotal, cc)}</span>
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
};


// ════════════════════════════════════════════
// COMPONENT 2: VIEWER PANEL
// ════════════════════════════════════════════
const AzureInvoice_Report_Viewer = ({ invoice, onAnalysis }) => {
    const [loading, setLoading] = useState(false);
    const [error,   setError]   = useState('');

    const handleAnalyze = async () => {
        setLoading(true);
        setError('');
        try {
            const { data } = await aitAnalyzeInvoice(invoice.Id);
            onAnalysis(data);
        } catch {
            setError('Analysis failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (!invoice) return (
        <div className="ait-panel-center">
            <div className="ait-vw-empty">
                <span className="ait-vw-empty-icon">📂</span>
                <span>Select an invoice from the left panel</span>
            </div>
        </div>
    );

    return (
        <div className="ait-panel-center">
            <div className="ait-vw-toolbar">
                <span className="ait-vw-filename" title={invoice.FileName}>
                    📄 {invoice.FileName}
                </span>
                <button
                    className="ait-vw-analyze-btn"
                    onClick={handleAnalyze}
                    disabled={loading}
                >
                    {loading
                        ? <><span className="ait-spinner" />Analyzing...</>
                        : '▶ Run Analysis'
                    }
                </button>
            </div>

            {error && <p className="ait-error">{error}</p>}

            <iframe
                key={invoice.Id}
                src={aitGetFileUrl(invoice.Id)}
                className="ait-vw-iframe"
                title="Invoice Preview"
            />
        </div>
    );
};


// ════════════════════════════════════════════
// COMPONENT 3: UPLOADER / LIST PANEL
// ════════════════════════════════════════════
const AzureInvoice_Report_Uploader = ({ onSelect, selectedId }) => {
    const [invoices,  setInvoices]  = useState([]);
    const [uploading, setUploading] = useState(false);
    const [error,     setError]     = useState('');
    const fileRef = useRef();

    const fetchList = async () => {
        try {
            const { data } = await aitGetInvoices();
            setInvoices(data);
        } catch {
            setError('Failed to load invoices.');
        }
    };

    useEffect(() => { fetchList(); }, []);

    const handleUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        e.target.value = '';
        setUploading(true);
        setError('');
        try {
            await aitUploadInvoice(file);
            await fetchList();
        } catch {
            setError('Upload failed. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    const formatDate = (dateStr) =>
        new Date(dateStr).toLocaleDateString('en-IN', {
            month: 'short', day: 'numeric', year: 'numeric'
        });

    return (
        <div className="ait-panel-left">
            <div className="ait-up-header">
                <h2 className="ait-up-title">📄 Invoices</h2>
                <input
                    type="file"
                    ref={fileRef}
                    style={{ display: 'none' }}
                    accept=".pdf,.png,.jpg,.jpeg"
                    onChange={handleUpload}
                />
                <button
                    className="ait-up-btn"
                    onClick={() => fileRef.current.click()}
                    disabled={uploading}
                >
                    {uploading
                        ? <><span className="ait-spinner" />Uploading...</>
                        : '+ Upload Invoice'
                    }
                </button>
                {error && <p className="ait-up-error">{error}</p>}
            </div>

            <div className="ait-up-list">
                {invoices.length === 0 ? (
                    <p className="ait-up-empty">
                        No invoices yet.<br />Upload one above.
                    </p>
                ) : (
                   invoices.map(inv => (
    <div
        key={inv.Id}
        className={`ait-up-item ${selectedId === inv.Id ? 'ait-up-item--active' : ''}`}
        onClick={() => onSelect(inv)}
    >
        <div className="ait-up-item-name" title={inv.FileName}>
            {inv.FileName}
        </div>

        <div className="ait-up-item-meta">
            <span>{formatDate(inv.UploadedAt)}</span>

            <span
                className={`ait-up-badge ait-up-badge--${inv.Status.toLowerCase()}`}
            >
                {inv.Status}
            </span>
        </div>
    </div>
))
                )}
            </div>
        </div>
    );
};


// ════════════════════════════════════════════
// MAIN EXPORT
// ════════════════════════════════════════════
const AzureInvoice_Report = () => {
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [analysisData,    setAnalysisData]    = useState(null);

    const handleSelect = (inv) => {
        setSelectedInvoice(inv);
        setAnalysisData(null);
    };

    return (
        <div className="ait-root">
            <div className="ait-app">
                <AzureInvoice_Report_Uploader
                    onSelect={handleSelect}
                    selectedId={selectedInvoice?.Id || null }
                />
                <AzureInvoice_Report_Viewer
                    invoice={selectedInvoice}
                    onAnalysis={setAnalysisData}
                />
                <AzureInvoice_Report_Result
                    data={analysisData}
                />
            </div>
        </div>
    );
};

export default AzureInvoice_Report;